<template>
  <div class="table">
    <table class="table__table" ref="table">
      <thead class="table__header">
        <tr class="table__row table__head-row">
          <th
            class="table__head table__cell"
            :class="`table__cell--align-${field.align || 'left'} ${
              field.sortable ? 'table__head--sortable' : ''
            }`"
            v-for="(field, index) in fields"
            :key="index"
            @click="headerClick(index)"
          >
            <div
              :class="`table__head-wrapper table__head-wrapper--align-${field.align}`"
            >
              <fai
                icon="sort"
                v-if="field.sortable"
                class="table__head-icon-sortble"
              ></fai>
              <span>{{ field.label }}</span>
              <fai
                v-if="field.sortable"
                class="table__head-sort-indicator"
                :class="
                  sortedBy === index
                    ? 'table__head-sort-indicator--visible'
                    : ''
                "
                :icon="sortMode === 'asc' ? 'sort-up' : 'sort-down'"
              ></fai>
            </div>
          </th>
        </tr>
      </thead>
      <tbody class="table__body">
        <tr
          class="table__row table__data-row"
          v-for="(row, index) of data"
          :key="index"
        >
          <td
            class="table__data table__cell"
            :class="cellClasses(field)"
            v-for="field in Object.keys(fields)"
            :key="field"
          >
            <div v-if="!fields[field].type || fields[field].type === 'string'">
              {{ row[field] }}
            </div>
            <div v-if="fields[field].type === 'date'">
              {{ dateToString(row[field]) }}
            </div>
            <div
              v-if="fields[field].type === 'gender'"
              class="table__data--gender-container"
            >
              <fai
                class="table__data--gender-icon"
                :icon="genderIcon(row[field])"
              ></fai>
            </div>
            <div
              v-if="fields[field].type === 'locale'"
              class="table__data--locale-flag-container"
            >
              <country-flag
                v-if="row[field]"
                class="table__data--locale-flag"
                :country="row[field].substring(3, 5).toLowerCase()"
                size="normal"
              ></country-flag>
            </div>
            <div
              v-if="fields[field].type === 'communicator'"
              class="table__data--communicator-container"
            >
              <fai
                v-if="row[field] === 1"
                class="table__data--communicator-icon"
                :icon="['fab', 'facebook-messenger']"
              ></fai>
              <fai
                v-if="row[field] === 2"
                class="table__data--communicator-icon"
                icon="globe"
              ></fai>
              <fai
                v-if="row[field] === 3"
                class="table__data--communicator-icon"
                style="left: 50%"
                icon="microphone-alt"
              ></fai>
            </div>
            <div
              v-if="fields[field].type === 'source'"
              class="table__data--source"
            >
              <fai
                class="table__data--source-icon"
                v-if="row[field] === 1"
                icon="question"
                :title="$t('component.table.source.unknown')"
              ></fai>
              <fai
                class="table__data--source-icon"
                v-if="row[field] === 2"
                icon="comment-alt"
                :title="$t('component.table.source.direct')"
              ></fai>
              <fai
                class="table__data--source-icon"
                v-if="row[field] === 3"
                icon="link"
                :title="$t('component.table.source.shortlink')"
              ></fai>
              <fai
                class="table__data--source-icon"
                v-if="row[field] === 4"
                icon="ad"
                :title="$t('component.table.source.ads')"
              ></fai>
              <fai
                class="table__data--source-icon"
                v-if="row[field] === 5"
                icon="plug"
                :title="$t('component.table.source.ccp')"
              ></fai>
              <fai
                class="table__data--source-icon"
                v-if="row[field] === 6"
                icon="binoculars"
                :title="$t('component.table.source.discover')"
              ></fai>
              <fai
                class="table__data--source-icon"
                v-if="row[field] === 7"
                icon="qrcode"
                :title="$t('component.table.source.code')"
              ></fai>
            </div>
            <div
              v-if="fields[field].type === 'buttons'"
              class="table__data--buttons"
            >
              <div
                :class="[
                  'table__button',
                  { [`table__button__${button.color}`]: button.color },
                ]"
                v-for="button in fields.operations.list"
                :key="button.eventName"
                :title="button.name"
                @click="$emit(button.eventName, row)"
              >
                <fai :icon="button.icon"></fai>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
      <tfoot class="table__foot">
        <td :colspan="`${Object.keys(fields).length}`">
          <div class="table__foot-wrapper">
            <div class="table__foot-counter table__foot-element">
              {{ $t("common.showing") }}
              {{ page * itemsOnPage - itemsOnPage + data.length }}
              {{ $t("common.of") }} {{ amount }}
            </div>
            <div class="table__foot-page-counter table__foot-element">
              <fai
                icon="fast-backward"
                class="table__foot-control"
                @click="pageChange(-5)"
              ></fai>
              <fai
                icon="step-backward"
                class="table__foot-control"
                @click="pageChange(-1)"
              ></fai>
              <span
                >{{ $t("common.page") }} {{ page }} {{ $t("common.of") }}
                {{ Math.ceil(amount / itemsOnPage) }}</span
              >
              <fai
                icon="step-forward"
                class="table__foot-control"
                @click="pageChange(1)"
              ></fai>
              <fai
                icon="fast-forward"
                class="table__foot-control"
                @click="pageChange(5)"
              ></fai>
            </div>
            <div
              v-if="!hideSearchField"
              class="table__foot-search table__foot-element"
            >
              <in
                :placeholder="$t('common.search')"
                v-model="searchPhrase"
                class="table__foot-search-element"
                @enter="$emit('search', searchPhrase)"
              ></in>
              <fai
                class="table__foot-search-icon"
                icon="search"
                @click="$emit('search', searchPhrase)"
              ></fai>
            </div>
            <div v-else class="table__foot-element"></div>
          </div>
        </td>
      </tfoot>
    </table>
  </div>
</template>

<script>
export default {
  props: {
    fields: Object,
    data: Array,
    amount: Number,
    page: Number,
    sortedBy: String,
    sortMode: String,
    itemsOnPage: {
      type: Number,
      default: 50,
    },
    hideSearchField: Boolean,
  },

  data() {
    return {
      searchPhrase: "",
      tableWidth: 0,
    };
  },

  methods: {
    cellClasses(field) {
      return `table__data--${this.fields[field].type} table__cell--align-${
        this.fields[field].align || "left"
      }`;
    },

    genderIcon(val) {
      if (val === "male") return "mars";
      else if (val === "female") return "venus";
      else return "genderless";
    },

    pageChange(n) {
      let target = this.page + n;
      if (target < 1) target = 1;
      if (target > Math.ceil(this.amount / this.itemsOnPage))
        target = Math.ceil(this.amount / this.itemsOnPage);
      this.$emit("pageChange", target);
    },

    headerClick(header) {
      if (!this.fields[header].sortable) return;
      this.$emit("sort", header);
    },

    dateToString(v) {
      if (!v) return "-";
      return new Date(v).toLocaleString();
    },
  },
};
</script>

<style lang="scss" scoped>
.table {
  margin: 2% auto 0 auto;
  height: 92%;
  position: relative;
  overflow-y: scroll;
  width: 100%;

  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    border-radius: 10px;
    background-color: #f5f5f5;
  }

  &::-webkit-scrollbar {
    width: 3px;
    margin-left: -10px;
    background-color: #f5f5f5;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: $inactiveGrey;
  }

  &__table {
    width: 100%;
    margin: auto;
    border-collapse: separate;
    border-spacing: 0;
  }

  &__header-mask {
    height: 38px;
    position: fixed;
    background: $bgGrey;
  }

  &__data-row {
    background-color: #fcfcfc;
    transition: background-color 0.2s ease;
    will-change: background-color;

    &:hover {
      background-color: $bgLightGrey;
    }
  }

  &__header {
    font-family: "Lato";
    background: $bgGrey;
  }

  &__data {
    border-top: 4px solid $bgGrey;
    padding: 15px 10px !important;

    &--locale {
      text-align: center;
    }

    &--locale-flag {
      position: absolute;
      left: 42%;
    }

    &--locale-flag-container {
      transform: scale(0.75);
      margin-top: -2px;
    }

    &--gender-container,
    &--communicator-container,
    &--source {
      position: relative;
    }

    &--gender,
    &--communicator,
    &--source {
      text-align: center;
    }

    &--gender-icon {
      font-size: 1.6em;
      position: absolute;
      left: 47%;
    }

    &--communicator-icon {
      font-size: 1.33em;
      position: absolute;
      left: 47%;
    }

    &--source-icon {
      position: absolute;
      font-size: 1.33em;
    }

    &--buttons {
      @include alignedFlex(center);
    }
  }

  &__cell {
    padding: 5px 10px;
    vertical-align: top;

    &--align-left {
      text-align: left;
    }

    &--align-center {
      text-align: center;
    }

    &--align-right {
      text-align: right;
    }
  }

  &__head {
    height: 28px;
    position: -webkit-sticky;
    position: sticky;
    top: 0;
    vertical-align: middle;
    border-bottom: 1px solid $borderGrey;
    background: $bgGrey;
    z-index: 10;

    &--sortable {
      cursor: pointer;
    }
  }

  &__head-wrapper {
    &--align-left {
      @include alignedFlex(flex-start);
    }

    &--align-center {
      @include alignedFlex(center);
    }

    &--align-right {
      @include alignedFlex(flex-end);
    }
  }

  &__head-icon-sortble {
    display: block;
    margin-right: 5px;
  }

  &__head-sort-indicator {
    display: block;
    margin-left: 5px;
    opacity: 0;

    &--visible {
      opacity: 1;
    }
  }

  &__foot,
  &__foot td {
    position: -webkit-sticky;
    position: sticky;
    bottom: 0;
    padding: 0 15px;
    font-size: 0.95em;
    background: $bgLightGrey;
    border-top: 4px solid $bgGrey;
    height: 49px;
  }

  &__foot-wrapper {
    @include alignedFlex(space-between);
  }

  &__foot-element {
    width: 33.33%;
  }

  &__foot-page-counter {
    text-align: center;

    & > * {
      margin: 0 5px;
    }
  }

  &__foot-control {
    cursor: pointer;
  }

  &__foot-search {
    @include alignedFlex(flex-end);
  }

  &__foot-search-element {
    width: 50%;
  }

  &__foot-search-icon {
    font-size: 1.2em;
    margin-left: 10px;
    margin-top: 4px;
    cursor: pointer;
    color: inherit;
    transition: color 0.2s ease;
    will-change: color;

    &:hover {
      color: $primaryHover;
    }
  }

  &__button {
    &__red {
      background-color: $danger;
      &:hover {
        background-color: $dangerHover !important;
      }
    }

    &__green {
      background-color: $success;
      &:hover {
        background-color: $successHover !important;
      }
    }

    @include alignedFlex();
    background-color: $primary;
    margin: 0 3px;
    width: 24px;
    height: 24px;
    color: white;
    border-radius: 4px;
    transition: background-color 0.2s ease;
    cursor: pointer;
    &:hover {
      background-color: $primaryHover;
    }
  }
}
</style>
